import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Loader2, Send, User, MapPin, Briefcase, DollarSign, ChevronRight, CheckCircle } from "lucide-react";
import { getMyProfile, submitCreditApp, CreditAppPayload } from "@/Calls/callCustomer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const US_STATES = [
    "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
    "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
    "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
    return (
        <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                {label}{required && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            {children}
        </div>
    );
}

type Step = 0 | 1 | 2 | 3;

const STEPS = [
    { label: "Personal", icon: User },
    { label: "Address", icon: MapPin },
    { label: "Employment", icon: Briefcase },
    { label: "Income", icon: DollarSign },
];

export default function CustomerCreditAppPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dealId = searchParams.get("dealId") ? Number(searchParams.get("dealId")) : undefined;

    const [step, setStep] = useState<Step>(0);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { register, setValue, watch, handleSubmit, reset } = useForm<CreditAppPayload>();

    useEffect(() => {
        getMyProfile()
            .then((p) => {
                reset({
                    app_deal_id: dealId,
                    applicant_fname: p.customer_fname ?? "",
                    applicant_mname: p.customer_mname ?? "",
                    applicant_lname: p.customer_lname ?? "",
                    applicant_dob: p.customer_dob ?? "",
                    applicant_cell_phone: p.customer_cellphone ?? "",
                    applicant_main_phone: p.customer_phoneno ?? "",
                    applicant_driver_licenses_number: p.customer_dlno ?? "",
                    applicant_driver_state_issued: p.customer_dlstate ?? "",
                    applicant_driver_licenses_expdate: p.customer_dlexpdate ?? "",
                    applicant_present_address1: p.customer_home_addr1 ?? "",
                    applicant_present_addr_city: p.customer_home_city ?? "",
                    applicant_present_addr_state: p.customer_home_state ?? "",
                    applicant_present_addr_zip: p.customer_home_zip ?? "",
                    applicant_addr_years: p.customer_home_live_years ?? "",
                    applicant_addr_months: p.customer_home_live_months ?? "",
                    applicant_employer1_name: p.customer_employer_name ?? "",
                    applicant_employer1_addr: p.customer_employer_addr1 ?? "",
                    applicant_employer1_city: p.customer_employer_city ?? "",
                    applicant_employer1_state: p.customer_employer_state ?? "",
                    applicant_employer1_zip: p.customer_employer_zip ?? "",
                    applicant_employer1_phone: p.customer_employer_phone ?? "",
                    applicant_employer1_dateofhire: p.customer_employer_hiredate ?? "",
                    applicant_employer1_work_years: p.customer_employer_years ?? "",
                    applicant_employer1_work_months: p.customer_employer_months ?? "",
                    applicant_employer1_salary_bringhome: p.customer_net_income ?? "",
                    credit_app_source: "CUSTOMER_PORTAL",
                });
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [dealId, reset]);

    async function onSubmit(data: CreditAppPayload) {
        setSubmitting(true);
        setError(null);
        try {
            await submitCreditApp(data);
            setDone(true);
            setTimeout(() => navigate("/customer/documents"), 2500);
        } catch (e: any) {
            setError(e?.response?.data?.error ?? "Submission failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (done) {
        return (
            <div className="max-w-md mx-auto text-center py-24 space-y-4">
                <div className="rounded-full bg-emerald-100 p-4 w-16 h-16 mx-auto flex items-center justify-center">
                    <CheckCircle className="h-8 w-8 text-emerald-600" />
                </div>
                <h2 className="text-xl font-semibold">Application Submitted!</h2>
                <p className="text-muted-foreground text-sm">
                    Your credit application has been submitted. Redirecting to your documents…
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="max-w-2xl mx-auto space-y-6">
            <div>
                <h1 className="text-2xl font-light uppercase tracking-wide">Credit Application</h1>
                <p className="text-muted-foreground text-sm mt-1">
                    {dealId ? `Applying for Deal #${dealId} · ` : ""}
                    Your profile information has been pre-filled. Review and submit.
                </p>
            </div>

            {/* Step indicator */}
            <div className="flex items-center gap-0">
                {STEPS.map((s, i) => {
                    const Icon = s.icon;
                    const active = step === i;
                    const done = step > i;
                    return (
                        <div key={i} className="flex items-center flex-1">
                            <button
                                type="button"
                                onClick={() => setStep(i as Step)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${active ? "bg-indigo-600 text-white" : done ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}
                            >
                                <Icon className="h-3 w-3" />
                                {s.label}
                            </button>
                            {i < STEPS.length - 1 && <div className="flex-1 h-px bg-border mx-1" />}
                        </div>
                    );
                })}
            </div>

            {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>
            )}

            {/* Step 0 — Personal */}
            {step === 0 && (
                <Card className="p-5 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <Field label="First Name" required>
                            <Input {...register("applicant_fname")} placeholder="First" required />
                        </Field>
                        <Field label="Middle Name">
                            <Input {...register("applicant_mname")} placeholder="Middle" />
                        </Field>
                        <Field label="Last Name" required>
                            <Input {...register("applicant_lname")} placeholder="Last" required />
                        </Field>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <Field label="Date of Birth" required>
                            <Input {...register("applicant_dob")} placeholder="MM/DD/YYYY" required />
                        </Field>
                        <Field label="Cell Phone">
                            <Input {...register("applicant_cell_phone")} placeholder="(555) 555-5555" />
                        </Field>
                        <Field label="Home Phone">
                            <Input {...register("applicant_main_phone")} placeholder="(555) 555-5555" />
                        </Field>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="SSN (Last 4 digits)">
                            <Input {...register("applicant_ssn4")} placeholder="XXXX" maxLength={4} />
                        </Field>
                        <Field label="Email Address">
                            <Input {...register("applicant_email_address")} placeholder="you@example.com" type="email" />
                        </Field>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <Field label="Driver's License #">
                            <Input {...register("applicant_driver_licenses_number")} placeholder="DL12345678" />
                        </Field>
                        <Field label="State Issued">
                            <Select onValueChange={(v) => setValue("applicant_driver_state_issued", v)} value={watch("applicant_driver_state_issued") ?? ""}>
                                <SelectTrigger><SelectValue placeholder="State" /></SelectTrigger>
                                <SelectContent>
                                    {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="DL Expiration">
                            <Input {...register("applicant_driver_licenses_expdate")} placeholder="MM/DD/YYYY" />
                        </Field>
                    </div>
                </Card>
            )}

            {/* Step 1 — Address */}
            {step === 1 && (
                <Card className="p-5 space-y-4">
                    <Field label="Street Address" required>
                        <Input {...register("applicant_present_address1")} placeholder="123 Main St" required />
                    </Field>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div className="col-span-2">
                            <Field label="City" required>
                                <Input {...register("applicant_present_addr_city")} placeholder="City" required />
                            </Field>
                        </div>
                        <Field label="State" required>
                            <Select onValueChange={(v) => setValue("applicant_present_addr_state", v)} value={watch("applicant_present_addr_state") ?? ""}>
                                <SelectTrigger><SelectValue placeholder="State" /></SelectTrigger>
                                <SelectContent>
                                    {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="ZIP" required>
                            <Input {...register("applicant_present_addr_zip")} placeholder="12345" required />
                        </Field>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <Field label="Years at Address">
                            <Input {...register("applicant_addr_years")} placeholder="0" type="number" min="0" />
                        </Field>
                        <Field label="Months at Address">
                            <Input {...register("applicant_addr_months")} placeholder="0" type="number" min="0" max="11" />
                        </Field>
                        <Field label="Own / Rent / Other">
                            <Select onValueChange={(v) => setValue("applicant_buy_own_rent_other", v)} value={watch("applicant_buy_own_rent_other") ?? ""}>
                                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="OWN">Own</SelectItem>
                                    <SelectItem value="RENT">Rent</SelectItem>
                                    <SelectItem value="OTHER">Other</SelectItem>
                                </SelectContent>
                            </Select>
                        </Field>
                    </div>
                    <Field label="Monthly Housing Payment">
                        <Input {...register("applicant_house_payment")} placeholder="0.00" type="number" step="0.01" min="0" />
                    </Field>
                </Card>
            )}

            {/* Step 2 — Employment */}
            {step === 2 && (
                <Card className="p-5 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Employment Status">
                            <Select onValueChange={(v) => setValue("applicant_employment_status", v)} value={watch("applicant_employment_status") ?? ""}>
                                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="FULL_TIME">Full Time</SelectItem>
                                    <SelectItem value="PART_TIME">Part Time</SelectItem>
                                    <SelectItem value="SELF_EMPLOYED">Self Employed</SelectItem>
                                    <SelectItem value="RETIRED">Retired</SelectItem>
                                    <SelectItem value="OTHER">Other</SelectItem>
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Employer Name">
                            <Input {...register("applicant_employer1_name")} placeholder="Company Name" />
                        </Field>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Employer Address">
                            <Input {...register("applicant_employer1_addr")} placeholder="123 Business Ave" />
                        </Field>
                        <Field label="Employer Phone">
                            <Input {...register("applicant_employer1_phone")} placeholder="(555) 555-5555" />
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div className="col-span-2">
                            <Field label="City">
                                <Input {...register("applicant_employer1_city")} placeholder="City" />
                            </Field>
                        </div>
                        <Field label="State">
                            <Select onValueChange={(v) => setValue("applicant_employer1_state", v)} value={watch("applicant_employer1_state") ?? ""}>
                                <SelectTrigger><SelectValue placeholder="State" /></SelectTrigger>
                                <SelectContent>
                                    {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="ZIP">
                            <Input {...register("applicant_employer1_zip")} placeholder="12345" />
                        </Field>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <Field label="Hire Date">
                            <Input {...register("applicant_employer1_dateofhire")} placeholder="MM/DD/YYYY" />
                        </Field>
                        <Field label="Years">
                            <Input {...register("applicant_employer1_work_years")} placeholder="0" type="number" min="0" />
                        </Field>
                        <Field label="Months">
                            <Input {...register("applicant_employer1_work_months")} placeholder="0" type="number" min="0" max="11" />
                        </Field>
                    </div>
                    <Field label="Job Title / Position">
                        <Input {...register("applicant_employer1_position")} placeholder="e.g. Sales Manager" />
                    </Field>
                </Card>
            )}

            {/* Step 3 — Income */}
            {step === 3 && (
                <Card className="p-5 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Monthly Take-Home Pay" required>
                            <Input {...register("applicant_employer1_salary_bringhome")} placeholder="0.00" type="number" step="0.01" min="0" required />
                        </Field>
                        <Field label="Pay Frequency">
                            <Select onValueChange={(v) => setValue("applicant_employer1_payday_freq", v)} value={watch("applicant_employer1_payday_freq") ?? ""}>
                                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="WEEKLY">Weekly</SelectItem>
                                    <SelectItem value="BIWEEKLY">Bi-Weekly</SelectItem>
                                    <SelectItem value="SEMIMONTHLY">Semi-Monthly</SelectItem>
                                    <SelectItem value="MONTHLY">Monthly</SelectItem>
                                </SelectContent>
                            </Select>
                        </Field>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Other Income Amount">
                            <Input {...register("applicant_other_income_amount")} placeholder="0.00" type="number" step="0.01" min="0" />
                        </Field>
                        <Field label="Other Income Source">
                            <Input {...register("applicant_other_income_source")} placeholder="e.g. Rental income" />
                        </Field>
                    </div>
                    <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                        By submitting this application you authorize the dealer to obtain a credit report and verify the information provided. This is not a commitment to lend.
                    </div>
                </Card>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between pb-6">
                {step > 0 ? (
                    <Button type="button" variant="outline" onClick={() => setStep((step - 1) as Step)}>
                        Back
                    </Button>
                ) : <div />}

                {step < 3 ? (
                    <Button type="button" onClick={() => setStep((step + 1) as Step)} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                        Continue <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                ) : (
                    <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white px-8">
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                        Submit Application
                    </Button>
                )}
            </div>
        </form>
    );
}
